import { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { categories, mainSiteUrl } from '@/mocks/categories';

export default function Navbar() {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setIsDropdownOpen(false);
      }
    };
    window.addEventListener('scroll', handleScroll);
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      window.removeEventListener('scroll', handleScroll);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleCategoryClick = () => {
    setIsDropdownOpen(false);
    setIsMobileMenuOpen(false);
  };

  const handleNavClick = () => {
    setIsMobileMenuOpen(false);
  };

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled
          ? 'bg-background-50/95 backdrop-blur-md'
          : 'bg-transparent'
      }`}
    >
      <div className="px-4 md:px-6">
        <div className="flex items-center justify-between h-16">
          <Link
            to="/"
            onClick={handleNavClick}
            className="flex items-center gap-2 group"
          >
            <span className={`font-heading text-lg md:text-xl font-bold whitespace-nowrap transition-colors duration-300 ${
              isScrolled ? 'text-foreground-950' : 'text-white'
            }`}>
              Sugargoo Spreadsheet
            </span>
          </Link>

          <div className="hidden md:flex items-center gap-6">
            <Link
              to="/"
              className={`text-sm font-medium transition-colors whitespace-nowrap ${
                isScrolled ? 'text-foreground-700 hover:text-primary-600' : 'text-white/85 hover:text-white'
              }`}
            >
              Home
            </Link>

            <div className="relative" ref={dropdownRef}>
              <button
                onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                className={`flex items-center gap-1.5 text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                  isScrolled ? 'text-foreground-700 hover:text-primary-600' : 'text-white/85 hover:text-white'
                }`}
              >
                Categories
                <i className={`ri-arrow-down-s-line text-base transition-transform duration-200 ${isDropdownOpen ? 'rotate-180' : ''}`}></i>
              </button>

              {isDropdownOpen && (
                <div className="absolute top-full left-0 mt-2 w-64 bg-background-50 rounded-lg border border-background-200/70 overflow-hidden max-h-[420px] overflow-y-auto">
                  {categories.map((cat) => (
                    <a
                      key={cat.slug}
                      href={cat.url}
                      target="_blank"
                      rel="noopener nofollow"
                      onClick={handleCategoryClick}
                      className="block w-full text-left px-4 py-3 text-sm text-foreground-700 hover:bg-primary-50 hover:text-primary-700 transition-colors whitespace-nowrap border-b border-background-200/50 last:border-b-0 cursor-pointer"
                    >
                      {cat.name}
                    </a>
                  ))}
                </div>
              )}
            </div>

            <Link
              to="/article/sugargoo-spreadsheet-complete-guide"
              className={`text-sm font-medium transition-colors whitespace-nowrap ${
                isScrolled ? 'text-foreground-700 hover:text-primary-600' : 'text-white/85 hover:text-white'
              }`}
            >
              Shopping Guide
            </Link>

            <Link
              to="/article/sugargoo-shipping-guide"
              className={`text-sm font-medium transition-colors whitespace-nowrap ${
                isScrolled ? 'text-foreground-700 hover:text-primary-600' : 'text-white/85 hover:text-white'
              }`}
            >
              Shipping
            </Link>

            <a
              href={mainSiteUrl}
              target="_blank"
              rel="noopener nofollow"
              className={`text-sm font-medium transition-colors whitespace-nowrap ${
                isScrolled ? 'text-foreground-700 hover:text-primary-600' : 'text-white/85 hover:text-white'
              }`}
            >
              Main Platform
            </a>
          </div>

          <div className="hidden md:flex items-center gap-3">
            <a
              href={mainSiteUrl}
              target="_blank"
              rel="noopener nofollow"
              className="inline-flex items-center px-4 py-2 text-sm font-semibold rounded-md bg-primary-500 text-background-50 hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
            >
              Buy It Now
            </a>
          </div>

          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className={`md:hidden w-10 h-10 flex items-center justify-center cursor-pointer transition-colors duration-300 ${
              isScrolled ? 'text-foreground-700' : 'text-white'
            }`}
          >
            <i className={`text-xl ${isMobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
          </button>
        </div>
      </div>

      {isMobileMenuOpen && (
        <div className="md:hidden bg-background-50 border-t border-background-200/70">
          <div className="px-4 py-4 max-h-[70vh] overflow-y-auto">
            <div className="flex flex-col gap-0.5">
              <Link
                to="/"
                onClick={handleNavClick}
                className="flex items-center gap-3 px-3 py-3 text-sm font-medium text-foreground-700 hover:bg-primary-50 rounded-lg transition-colors whitespace-nowrap"
              >
                <div className="w-8 h-8 flex items-center justify-center rounded-full bg-primary-100">
                  <i className="ri-home-4-line text-primary-600 text-sm"></i>
                </div>
                Home
              </Link>

              <Link
                to="/article/sugargoo-spreadsheet-complete-guide"
                onClick={handleNavClick}
                className="flex items-center gap-3 px-3 py-3 text-sm font-medium text-foreground-700 hover:bg-primary-50 rounded-lg transition-colors whitespace-nowrap"
              >
                <div className="w-8 h-8 flex items-center justify-center rounded-full bg-accent-100">
                  <i className="ri-book-open-line text-accent-600 text-sm"></i>
                </div>
                Shopping Guide
              </Link>

              <Link
                to="/article/sugargoo-shipping-guide"
                onClick={handleNavClick}
                className="flex items-center gap-3 px-3 py-3 text-sm font-medium text-foreground-700 hover:bg-primary-50 rounded-lg transition-colors whitespace-nowrap"
              >
                <div className="w-8 h-8 flex items-center justify-center rounded-full bg-secondary-100">
                  <i className="ri-ship-line text-secondary-600 text-sm"></i>
                </div>
                Shipping Guide
              </Link>

              <div className="border-t border-background-200/70 my-2"></div>

              <div className="px-3 py-2 text-xs font-semibold text-foreground-400 uppercase tracking-wider whitespace-nowrap">
                Browse Categories
              </div>

              <div className="grid grid-cols-2 gap-1">
                {categories.map((cat) => (
                  <a
                    key={cat.slug}
                    href={cat.url}
                    target="_blank"
                    rel="noopener nofollow"
                    onClick={handleCategoryClick}
                    className="flex items-center gap-2 px-3 py-2.5 text-sm text-foreground-600 hover:bg-primary-50 hover:text-primary-700 rounded-lg transition-colors whitespace-nowrap cursor-pointer"
                  >
                    <div className="w-7 h-7 rounded-md overflow-hidden flex-shrink-0">
                      <img
                        src={cat.imageUrl}
                        alt={cat.name}
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <span className="truncate">{cat.name}</span>
                  </a>
                ))}
              </div>

              <div className="border-t border-background-200/70 my-2"></div>

              <a
                href={mainSiteUrl}
                target="_blank"
                rel="noopener nofollow"
                onClick={handleNavClick}
                className="flex items-center gap-3 px-3 py-3 text-sm font-medium text-foreground-700 hover:bg-primary-50 rounded-lg transition-colors whitespace-nowrap"
              >
                <div className="w-8 h-8 flex items-center justify-center rounded-full bg-background-200">
                  <i className="ri-external-link-line text-foreground-600 text-sm"></i>
                </div>
                Visit Main Platform
              </a>

              <a
                href={mainSiteUrl}
                target="_blank"
                rel="noopener nofollow"
                onClick={handleNavClick}
                className="mt-2 flex items-center justify-center gap-2 px-4 py-3 text-sm font-semibold rounded-lg bg-primary-500 text-background-50 hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
              >
                <i className="ri-shopping-bag-line"></i>
                Buy It Now
              </a>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
}